import { useEffect, useRef } from "react";
import { useTheme } from "styled-components";
import { AUTH_CAPTCHA_CHALLENGE_ID } from "testIds";
import {
  loadCaptchaScript,
  type CaptchaFailureStage,
} from "utils/captchaScript";

type TurnstileOptions = {
  sitekey: string;
  action: string;
  theme: "dark" | "light";
  size: "flexible";
  appearance: "interaction-only";
  callback: (token: string) => void;
  "error-callback": (errorCode?: string) => void;
  "expired-callback": () => void;
  "timeout-callback": () => void;
};

type TurnstileApi = {
  render: (container: HTMLElement, options: TurnstileOptions) => string;
  remove: (widgetId: string) => void;
};

declare global {
  interface Window {
    turnstile?: TurnstileApi;
  }
}

const TurnstileChallenge = ({
  sitekey,
  onSuccess,
  onError,
}: {
  sitekey: string;
  onSuccess: (token: string) => void;
  onError: (stage: CaptchaFailureStage, errorCode?: string) => void;
}) => {
  const container = useRef<HTMLDivElement>(null);
  const { isDarkMode } = useTheme();
  const callbacks = useRef({ onSuccess, onError });
  callbacks.current = { onSuccess, onError };

  useEffect(() => {
    let active = true;
    let widgetId: string | undefined;
    let api: TurnstileApi | undefined;
    let failureStage: CaptchaFailureStage = "script_load";
    const timeout = window.setTimeout(() => {
      if (active) callbacks.current.onError("challenge_timeout");
    }, 60000);
    const fail = (stage: CaptchaFailureStage, errorCode?: string) => {
      if (active) callbacks.current.onError(stage, errorCode);
    };
    loadCaptchaScript(TURNSTILE_SCRIPT)
      .then(() => {
        if (!active || !container.current) return;
        failureStage = "sdk_initialization";
        api = window.turnstile;
        if (!api) throw new Error("Captcha is unavailable");
        failureStage = "widget_render";
        widgetId = api.render(container.current, {
          sitekey,
          action: "authenticate",
          theme: isDarkMode ? "dark" : "light",
          size: "flexible",
          appearance: "interaction-only",
          callback: (token) => {
            if (active) callbacks.current.onSuccess(token);
          },
          "error-callback": (errorCode) => fail("widget_error", errorCode),
          "expired-callback": () => fail("token_expired"),
          "timeout-callback": () => fail("interaction_timeout"),
        });
      })
      .catch(() => fail(failureStage));
    return () => {
      active = false;
      window.clearTimeout(timeout);
      if (widgetId !== undefined) api?.remove(widgetId);
    };
  }, [sitekey, isDarkMode]);

  return <div ref={container} data-testid={AUTH_CAPTCHA_CHALLENGE_ID} />;
};

export default TurnstileChallenge;

const TURNSTILE_SCRIPT =
  "https://challenges.cloudflare.com/turnstile/v0/api.js?render=explicit";
